import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PaymentModal from "../components/PaymentModal";
import ReviewModal from "../components/ReviewModal";
import ReferralSection from "../components/ReferralSection";
import VehicleDetailModal from "../components/VehicleDetailModal";
import { VehicleStatusBadge, VehicleStatusStrip } from "../components/VehicleStatusBadge";
import { PassengerRideCard } from "../components/passenger/PassengerRideCard";
import { PassengerBookingCard } from "../components/passenger/PassengerBookingCard";
import FavoritePlacesManager from "../components/passenger/FavoritePlacesManager";
import NotificationBell from "../components/NotificationBell";
import ExpressCourseCard from "../components/ExpressCourseCard";
import PassengerProfile from "../components/PassengerProfile";
import PassengerWallet from "../components/PassengerWallet";
import PassengerPacks from "../components/PassengerPacks";
import PassengerBottomNav from "../components/PassengerBottomNav";
import PassengerHome from "./PassengerHome";
import RentalVehiclesTab from "../components/RentalVehiclesTab";
import { useWebSocketNotifications } from "../hooks/useWebSocket";
import { usePushNotifications } from "../hooks/usePushNotifications";
import NotificationsActivateButton from "../components/NotificationsActivateButton";
import { usePassengerData } from "../hooks/usePassengerData";
import { Car, History, CheckCircle, KeyRound, Search, SlidersHorizontal, Bell, Gift, User, Wallet, Hourglass, Zap, MapPin } from "lucide-react";
import { toast } from "sonner";

const CONDITION_LABELS = { neuf: "Neuf", bon_etat: "Bon etat", acceptable: "Acceptable" };
const CONDITION_STYLES = { neuf: "bg-[#10B981]/80 text-white", bon_etat: "bg-[#FACC15]/80 text-[#0A2540]", acceptable: "bg-[#64748B]/80 text-white" };
const VEHICLE_TYPE_LABELS = { sedan: "Berline", suv: "SUV", "4x4": "4x4", minibus: "Minibus", pickup: "Pick-up" };

function RideSearchPanel({ searchDep, setSearchDep, searchDest, setSearchDest, searchDate, setSearchDate, searchMinPrice, setSearchMinPrice, searchMaxPrice, setSearchMaxPrice, searchMinSeats, setSearchMinSeats, onSearch, onClear }) {
  const [showFilters, setShowFilters] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-4 mb-4" data-testid="rides-search-panel">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4 text-[#64748B]" />
        <span className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Rechercher un trajet</span>
        <button onClick={() => setShowFilters(!showFilters)} className="ml-auto text-xs text-[#0A2540] flex items-center gap-1 hover:text-[#FACC15]" data-testid="toggle-filters">
          <SlidersHorizontal className="w-3 h-3" /> {showFilters ? "Masquer filtres" : "Plus de filtres"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Input value={searchDep} onChange={e => setSearchDep(e.target.value)} placeholder="Ville depart" className="h-9 border-[#CBD5E1] text-sm" data-testid="search-departure" />
        <Input value={searchDest} onChange={e => setSearchDest(e.target.value)} placeholder="Ville destination" className="h-9 border-[#CBD5E1] text-sm" data-testid="search-destination" />
        <Input type="date" value={searchDate} onChange={e => setSearchDate(e.target.value)} className="h-9 border-[#CBD5E1] text-sm" data-testid="search-date" />
        <div className="flex gap-1">
          <Button size="sm" onClick={onSearch} className="bg-[#0A2540] text-white hover:bg-[#081b2e] flex-1 h-9" data-testid="search-btn"><Search className="w-3.5 h-3.5 mr-1" /> Chercher</Button>
          <Button size="sm" variant="outline" onClick={onClear} className="border-[#CBD5E1] h-9 px-2" data-testid="clear-search-btn">X</Button>
        </div>
      </div>
      {showFilters && (
        <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-[#CBD5E1]">
          <Input type="number" value={searchMinPrice} onChange={e => setSearchMinPrice(e.target.value)} placeholder="Prix min (XAF)" className="h-8 border-[#CBD5E1] text-xs" data-testid="search-min-price" />
          <Input type="number" value={searchMaxPrice} onChange={e => setSearchMaxPrice(e.target.value)} placeholder="Prix max (XAF)" className="h-8 border-[#CBD5E1] text-xs" data-testid="search-max-price" />
          <Input type="number" value={searchMinSeats} onChange={e => setSearchMinSeats(e.target.value)} placeholder="Places min" className="h-8 border-[#CBD5E1] text-xs" data-testid="search-min-seats" />
        </div>
      )}
    </div>
  );
}

function VehicleCard({ v, onClick }) {
  const rawImages = v.image_urls?.length > 0 ? v.image_urls : (v.image_url ? [v.image_url] : []);
  const mainImg = rawImages[0];
  const imgCount = rawImages.filter(Boolean).length;
  const fallbackImg = "https://images.unsplash.com/photo-1622850780969-b7547252bccc?w=400&h=250&fit=crop";
  const resolved = mainImg ? resolveImageUrl(mainImg) : "";
  const displayImg = (resolved && !resolved.includes("example.com")) ? resolved : fallbackImg;

  return (
    <div className="relative bg-white rounded-xl border border-[#CBD5E1] overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#FACC15]/40 transition-all group" onClick={onClick} data-testid={`passenger-vehicle-${v.id}`}>
      <VehicleStatusStrip status={v.operational_status} />
      <div className="relative overflow-hidden">
        <img src={displayImg} alt={`${v.brand} ${v.model}`} className="w-full h-64 object-contain bg-[#F8FAFC] group-hover:scale-105 transition-transform duration-300" loading="lazy" onError={(e) => { e.target.src = fallbackImg; }} />
        {imgCount > 1 && <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">{imgCount} photos</div>}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          <VehicleStatusBadge status={v.operational_status} />
          {v.vehicle_condition && (
            <div className={`text-[10px] px-2 py-0.5 rounded-full font-semibold backdrop-blur-sm ${CONDITION_STYLES[v.vehicle_condition] || CONDITION_STYLES.acceptable}`}>
              {CONDITION_LABELS[v.vehicle_condition] || "Acceptable"}
            </div>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-[#0A2540] mb-1" style={{ fontFamily: 'Outfit' }}>{v.brand} {v.model} <span className="text-xs font-normal text-[#64748B]">({v.year})</span></h3>
        <div className="flex items-center gap-2 text-xs text-[#64748B] mb-2 flex-wrap">
          <span>{v.seats || 4} places</span>
          <span className="w-1 h-1 rounded-full bg-[#CBD5E1]" />
          <span>{VEHICLE_TYPE_LABELS[v.vehicle_type] || v.vehicle_type}</span>
          {v.air_conditioning && <><span className="w-1 h-1 rounded-full bg-[#CBD5E1]" /><span className="text-[#10B981]">Clim</span></>}
          {v.city && <><span className="w-1 h-1 rounded-full bg-[#CBD5E1]" /><span className="text-[#10B981]">{v.city}</span></>}
          {typeof v.distance_km === "number" && (
            <>
              <span className="w-1 h-1 rounded-full bg-[#CBD5E1]" />
              <span className="text-[#3B82F6] font-semibold flex items-center gap-1" data-testid={`vehicle-distance-${v.id}`}>
                <MapPin className="w-3 h-3" />{v.distance_km.toFixed(1)} km
              </span>
            </>
          )}
          {v.driver_online === false && (
            <>
              <span className="w-1 h-1 rounded-full bg-[#CBD5E1]" />
              <span className="text-[#94A3B8] text-[10px]">Hors ligne</span>
            </>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div>
            <p className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{v.price_per_day?.toLocaleString()} <span className="text-xs font-normal text-[#64748B]">XAF/jour</span></p>
            {(v.price_city_tour > 0 || v.price_out_of_city > 0) && (
              <div className="flex gap-2 mt-0.5">
                {v.price_city_tour > 0 && <span className="text-[10px] text-[#3B82F6]">Ville: {v.price_city_tour.toLocaleString()} XAF</span>}
                {v.price_out_of_city > 0 && <span className="text-[10px] text-[#10B981]">Hors-ville: {v.price_out_of_city.toLocaleString()} XAF</span>}
              </div>
            )}
          </div>
          <span className="text-xs text-[#FACC15] font-semibold group-hover:text-[#0A2540] transition-colors">Voir details →</span>
        </div>
      </div>
    </div>
  );
}

export default function PassengerDashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("home");
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState("");
  const [mapRide, setMapRide] = useState(null);
  const [otpBooking, setOtpBooking] = useState(null);
  const [chatBookingId, setChatBookingId] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);

  // Search state
  const [searchDep, setSearchDep] = useState("");
  const [searchDest, setSearchDest] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searchMinPrice, setSearchMinPrice] = useState("");
  const [searchMaxPrice, setSearchMaxPrice] = useState("");
  const [searchMinSeats, setSearchMinSeats] = useState("");

  const { notifications, unreadCount } = useWebSocketNotifications(user?.id);
  const { subscribe: subscribePush } = usePushNotifications(user?.id, null);

  // Effective payment methods for this passenger (admin global + per-user)
  const [paymentMethods, setPaymentMethods] = useState({ tft: true, mtn: true, orange: true, cashback_rate: 0.02 });
  const [activePacks, setActivePacks] = useState([]);
  useEffect(() => {
    // Defer these two GETs when we're landing on the home page — they are
    // only needed once the passenger enters an actual booking flow.
    const isHome = !searchParams.get("tab") && !searchParams.get("rent_vehicle") && !searchParams.get("book_ride");
    const load = () => {
      api.get("/passenger/payment-methods").then(({ data }) => setPaymentMethods({
        tft: !!data.tft, mtn: !!data.mtn, orange: !!data.orange, cashback_rate: data.cashback_rate || 0.02
      })).catch(() => {});
      api.get("/passenger/packs/active").then(({ data }) => setActivePacks(data.packs || [])).catch(() => {});
    };
    if (isHome) {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 800));
      const h = idle(load);
      return () => { if (window.cancelIdleCallback && typeof h === "number") window.cancelIdleCallback(h); };
    }
    load();
  }, [searchParams]);
  const walletEnabled = !!paymentMethods.tft;

  const {
    rides, vehicles, bookings, loading, setLoading,
    fetchRides, fetchVehicles, fetchBookings,
    trackingBookingId, driverLocation,
    startLiveTracking, stopLiveTracking, cleanup,
  } = usePassengerData();

  // Auto-refresh bookings on notification
  useEffect(() => {
    const latest = notifications[0];
    if (latest?.type === "booking_status" || latest?.type === "new_message") {
      fetchBookings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, fetchBookings]);

  // Iter 76 — react to the driver's "Terminer la course" WebSocket event
  // so the passenger's Express card refreshes (frozen chronometer, new
  // total) at the same moment the female voice announcement plays.
  useEffect(() => {
    const onWs = (ev) => {
      const t = ev?.detail?.type;
      if (t === "trip_finished" || t === "booking_completed" || t === "booking_updated") {
        fetchBookings();
      }
    };
    window.addEventListener("trigo:ws-message", onWs);
    return () => window.removeEventListener("trigo:ws-message", onWs);
  }, [fetchBookings]);

  useEffect(() => {
    // Determine which tab the user is landing on. When it's "home" we can
    // fully defer the heavier dashboard fetches (rides/vehicles/bookings)
    // to keep the initial paint minimal — the home page only needs the
    // hero image + click hotspots, nothing else.
    const tabParam = searchParams.get("tab");
    const landingTab = tabParam || (searchParams.get("rent_vehicle") ? "vehicles" : "home");
    if (tabParam) setTab(tabParam);
    else if (searchParams.get("book_ride")) window.location.href = `/rides?book_ride=${searchParams.get("book_ride")}`;
    else if (searchParams.get("rent_vehicle")) setTab("vehicles");

    if (landingTab === "home") {
      // Defer background fetches to after paint so the hero image and
      // hotspots render immediately. Uses `requestIdleCallback` when
      // available, otherwise a short setTimeout so we still get the JS
      // event loop's first free tick.
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 600));
      const handle = idle(() => {
        fetchRides();
        fetchVehicles();
        fetchBookings();
      });
      return () => {
        if (window.cancelIdleCallback && typeof handle === "number") window.cancelIdleCallback(handle);
        cleanup();
      };
    }
    // For every other tab the data is required immediately.
    fetchRides();
    fetchVehicles();
    fetchBookings();
    return cleanup;
  }, [fetchRides, fetchVehicles, fetchBookings, searchParams, cleanup]);

  // Auto-open vehicle detail modal when rent_vehicle param is present
  useEffect(() => {
    const vehicleId = searchParams.get("rent_vehicle");
    if (vehicleId && vehicles.length > 0) {
      const found = vehicles.find(v => v.id === vehicleId);
      if (found) setSelectedVehicle(found);
    }
  }, [searchParams, vehicles]);

  const handleSearch = () => fetchRides(searchDep, searchDest, searchDate, searchMinPrice, searchMaxPrice, searchMinSeats);
  const clearSearch = () => {
    setSearchDep(""); setSearchDest(""); setSearchDate(""); setSearchMinPrice(""); setSearchMaxPrice(""); setSearchMinSeats("");
    fetchRides();
  };

  const bookRide = async (ride, opts = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ride_id: ride.id, booking_type: "ride", seats: 1,
        pickup_location: ride.departure, dropoff_location: ride.destination,
        payment_method: opts.paymentMethod || "",
      });
      setBookingSuccess(`Reservation confirmee ! Total: ${data.total_amount?.toLocaleString()} XAF`);
      setPaymentBooking(data);
      fetchBookings();
      fetchRides();
      setTab("bookings");
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const maybeSuggestFavorite = async (pickupGeo, dropoffGeo) => {
    // Purpose: after a successful booking, if the address the passenger just
    // used has already been visited ≥2 times AND isn't a favorite yet, we
    // proactively prompt them to save it. The heavy lifting (dedup by
    // rounded lat/lng, exclusion of existing favorites, count ≥ 2) lives on
    // the backend at /me/places/suggestions.
    if (!pickupGeo?.lat && !dropoffGeo?.lat) return;
    try {
      const { data } = await api.get("/me/places/suggestions");
      if (!Array.isArray(data) || data.length === 0) return;
      const matches = [pickupGeo, dropoffGeo]
        .filter((g) => g?.lat && g?.lng)
        .map((g) => data.find((s) => Math.abs(s.lat - g.lat) < 5e-4 && Math.abs(s.lng - g.lng) < 5e-4))
        .filter(Boolean);
      if (matches.length === 0) return;
      const sg = matches[0];
      toast("💡 Enregistrer ce lieu ?", {
        description: `Vous êtes déjà passé ${sg.count}× à « ${sg.address.split(",")[0]} ». Un tap et il apparaîtra dans vos favoris.`,
        duration: 10000,
        action: {
          label: "Enregistrer",
          onClick: async () => {
            try {
              await api.post("/me/places", {
                label: sg.address.split(",")[0].trim().slice(0, 60) || "Lieu fréquent",
                address: sg.address, lat: sg.lat, lng: sg.lng, icon: "custom",
              });
              toast.success("Ajouté à vos favoris");
            } catch (_e) { /* silent */ }
          },
        },
      });
    } catch (_e) { /* silent */ }
  };


  const rentVehicle = async (vehicle, opts = {}) => {
    const { rentalMode = "", rentalHours = 0, rentalDays = 1, startDate = "", endDate = "", startTime = "", endTime = "", departureCity = "", destinationCity = "", paymentMethod = "", pickupNeighborhood = "", pickupAddress = "", promoCode = "", hourlyDestination = "", hourlyDestinationCustom = "", pickupGeo = null, dropoffGeo = null } = opts;
    const sd = startDate || new Date().toISOString().split("T")[0];
    const ed = endDate || new Date(Date.now() + 86400000 * rentalDays).toISOString().split("T")[0];
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        vehicle_id: vehicle.id, booking_type: "rental", seats: 1,
        start_date: sd, end_date: ed,
        start_time: startTime, end_time: endTime,
        // The pickup / dropoff CITY defaults to where the vehicle is
        // registered — never "Yaounde" blanket-fallback. This ensures the
        // driver's dispatch address matches their actual base city.
        pickup_location: departureCity || vehicle.city || "Yaounde",
        dropoff_location: destinationCity || vehicle.city || "Yaounde",
        pickup_neighborhood: pickupNeighborhood,
        pickup_address: pickupGeo?.address || pickupAddress,
        pickup_lat: pickupGeo?.lat ?? null,
        pickup_lng: pickupGeo?.lng ?? null,
        dropoff_address: dropoffGeo?.address || "",
        dropoff_lat: dropoffGeo?.lat ?? null,
        dropoff_lng: dropoffGeo?.lng ?? null,
        rental_mode: rentalMode,
        rental_hours: rentalHours,
        rental_days: rentalDays,
        departure_city: departureCity,
        destination_city: destinationCity,
        payment_method: paymentMethod || "",
        promo_code: promoCode || "",
        hourly_destination: hourlyDestination || "",
        hourly_destination_custom: hourlyDestinationCustom || "",
      });
      const paidTft = data.payment_method === "tft" && data.payment_status === "paid";
      const paidPack = data.payment_method === "pack" && data.pack_applied;
      const cb = Number(data.tft_cashback_amount || 0);
      setBookingSuccess(
        paidPack
          ? `Location couverte par votre Pack ${data.pack_type} ! ${data.pack_hours_used}h débitées · ${data.total_amount?.toLocaleString()} F (${data.pack_rate_per_hour?.toLocaleString()} F/h au lieu du tarif standard).`
          : paidTft
          ? `Location payée via TFT ! ${data.tft_debit_amount?.toLocaleString()} TFT débité${cb > 0 ? ` · +${cb.toLocaleString()} TFT cashback crédité` : ""}.`
          : `Location confirmee ! Total: ${data.total_amount?.toLocaleString()} XAF`);
      if (!paidTft && !paidPack) setPaymentBooking(data);
      // refresh active packs to reflect deductions
      if (paidPack) {
        api.get("/passenger/packs/active").then(({ data: d }) => setActivePacks(d.packs || [])).catch(() => {});
      }
      fetchBookings();
      fetchVehicles();
      setTab("bookings");
      // Smart suggestion: if the address we just used has been used ≥2 times
      // AND isn't already a favorite, gently prompt the passenger to save it.
      maybeSuggestFavorite(pickupGeo, dropoffGeo);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] pb-24 sm:pb-0" data-testid="passenger-dashboard" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
      <div className="bg-[#0A2540] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-full bg-[#FACC15] flex items-center justify-center text-[#0A2540] text-lg font-extrabold shrink-0">
                {(user?.name || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#FACC15] font-bold">Bienvenue</p>
                <h1 className="text-lg sm:text-xl font-extrabold text-white truncate" style={{ fontFamily: "Outfit" }}>{user?.name}</h1>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <NotificationsActivateButton />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {bookingSuccess && (
          <div className="mb-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4 flex items-center gap-3" data-testid="booking-success-alert">
            <CheckCircle className="w-5 h-5 text-[#10B981]" />
            <p className="text-sm font-medium text-[#0A2540]">{bookingSuccess}</p>
          </div>
        )}

        {/* Post-login landing page. Passengers only. Cards navigate to the
            requested routes: /express, ?tab=vehicles, ?tab=bookings, ?tab=profile. */}
        {tab === "home" && (
          <div className="-mx-4 sm:mx-0" data-testid="passenger-home-wrapper">
            <PassengerHome />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className={tab === "home" ? "hidden" : ""}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
            <TabsList className="bg-white border border-[#CBD5E1] inline-flex w-max sm:w-auto">
              <TabsTrigger value="vehicles" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-vehicles">
                <Car className="w-4 h-4 mr-1" /> Location
              </TabsTrigger>
              <TabsTrigger value="express" className="data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A2540] text-xs sm:text-sm whitespace-nowrap" data-testid="tab-express">
                <Zap className="w-4 h-4 mr-1" /> Express
              </TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-bookings">
                <History className="w-4 h-4 mr-1" /> Réservations
                {unreadCount > 0 && <span className="ml-1 w-4 h-4 bg-[#EF4444] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>}
              </TabsTrigger>
              <TabsTrigger value="express-bookings" className="data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A2540] text-xs sm:text-sm whitespace-nowrap" data-testid="tab-express-bookings"><Zap className="w-4 h-4 mr-1" /> Courses Express</TabsTrigger>
              <TabsTrigger value="packs" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-packs">
                <Hourglass className="w-4 h-4 mr-1" /> Pack Heures
              </TabsTrigger>
              <TabsTrigger value="referral" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-referral">
                <Gift className="w-4 h-4 mr-1" /> Parrainage
              </TabsTrigger>
              <TabsTrigger value="places" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-places">
                <MapPin className="w-4 h-4 mr-1" /> Lieux
              </TabsTrigger>
              {walletEnabled && (
                <TabsTrigger value="wallet" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-wallet">
                  <Wallet className="w-4 h-4 mr-1" /> Portefeuille
                </TabsTrigger>
              )}
              <TabsTrigger value="profile" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-profile">
                <User className="w-4 h-4 mr-1" /> Profil
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="express">
            <div className="bg-gradient-to-br from-[#0A2540] to-[#0F2E4E] rounded-3xl p-6 sm:p-8 text-white" data-testid="express-launcher-card">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-[#FACC15] flex items-center justify-center shrink-0">
                  <Zap className="w-6 h-6 text-[#0A2540]" strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FACC15]">Trigo Express</p>
                  <h3 className="text-2xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>Commander une course VTC</h3>
                  <p className="text-sm text-white/75 mt-2 max-w-md">
                    Moto, berline ou confort — un chauffeur à proximité en quelques secondes. Paiement Cash, TFT, MTN MoMo ou Orange Money.
                  </p>
                </div>
              </div>
              <a
                href="/express"
                className="mt-6 inline-flex items-center justify-center h-14 w-full rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-extrabold text-base shadow-lg shadow-[#FACC15]/25 transition"
                style={{ fontFamily: "Outfit" }}
                data-testid="express-launcher-cta"
              >
                Commander maintenant →
              </a>
            </div>
          </TabsContent>

          <TabsContent value="vehicles">
            <RentalVehiclesTab
              renderVehicle={(v) => (
                <VehicleCard key={v.id} v={v} onClick={() => setSelectedVehicle(v)} />
              )}
              onVehicleClick={(v) => setSelectedVehicle(v)}
            />
            <VehicleDetailModal vehicle={selectedVehicle} open={!!selectedVehicle} onClose={() => setSelectedVehicle(null)} onRent={(v, opts) => { setSelectedVehicle(null); rentVehicle(v, opts); }} loading={loading} paymentMethods={paymentMethods} activePacks={activePacks} />
          </TabsContent>

          <TabsContent value="bookings">
            <div className="grid gap-4">
              {bookings.filter(b => b.booking_type !== "express").length === 0 ? (
                <p className="text-center py-10 text-[#64748B]">Aucune reservation</p>
              ) : bookings.filter(b => b.booking_type !== "express").map((b) => (
                <PassengerBookingCard key={b.id} booking={b} userId={user?.id} chatBookingId={chatBookingId} setChatBookingId={setChatBookingId} trackingBookingId={trackingBookingId} driverLocation={driverLocation} onShowOtp={setOtpBooking} onPay={setPaymentBooking} onReview={setReviewBooking} onStartTracking={startLiveTracking} onStopTracking={stopLiveTracking} onCancelled={fetchBookings} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="express-bookings">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#F2A93B]" />
              <h2 className="text-lg font-semibold text-[#0F2B46]" style={{ fontFamily: 'Outfit' }}>Mes courses Express</h2>
            </div>
            <div className="grid gap-4">
              {bookings.filter(b => b.booking_type === "express").length === 0 ? (
                <p className="text-center py-10 text-[#64748B]">
                  Aucune course Express pour le moment.<br />
                  <a href="/express" className="text-[#D98C1F] font-semibold underline">Commander une course Trigo Express →</a>
                </p>
              ) : bookings.filter(b => b.booking_type === "express").map((b) => (
                <ExpressCourseCard key={b.id} b={b} role="passenger" onUpdate={() => { fetchBookings(); }} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="referral">
            <div className="space-y-4">
              <ReferralSection user={user} />
              <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="push-optin-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#0A2540]/10 flex items-center justify-center"><Bell className="w-4 h-4 text-[#0A2540]" /></div>
                  <h3 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Notifications push</h3>
                </div>
                <p className="text-xs text-[#64748B] mb-3">Recevez des alertes meme quand le navigateur est ferme.</p>
                <Button size="sm" onClick={subscribePush} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid="enable-push-btn">
                  <Bell className="w-3.5 h-3.5 mr-1" /> Activer les notifications
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="wallet">
            {walletEnabled && <PassengerWallet />}
          </TabsContent>

          <TabsContent value="packs">
            <PassengerPacks />
          </TabsContent>

          <TabsContent value="profile">
            <PassengerProfile />
          </TabsContent>

          <TabsContent value="places">
            <FavoritePlacesManager />
          </TabsContent>
        </Tabs>
      </div>

      <PaymentModal open={!!paymentBooking} onClose={() => setPaymentBooking(null)} booking={paymentBooking} onSuccess={() => { fetchBookings(); fetchRides(); setBookingSuccess(""); }} />

      <Dialog open={!!otpBooking} onOpenChange={() => setOtpBooking(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="otp-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Code de verification OTP</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-[#0A2540] flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-[#FACC15]" />
            </div>
            <p className="text-xs text-[#64748B] mb-3">Communiquez ce code a votre chauffeur lors de la prise en charge.</p>
            <div className="flex justify-center gap-2 mb-4">
              {(otpBooking?.otp_code || "").split("").map((digit, i) => (
                <div key={`otp-digit-${i}`} className="w-14 h-16 rounded-xl bg-[#F8FAFC] border-2 border-[#0A2540] flex items-center justify-center">
                  <span className="text-3xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{digit}</span>
                </div>
              ))}
            </div>
            <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-lg p-3">
              <p className="text-xs text-[#0A2540] font-medium">Ne partagez ce code qu&apos;avec votre chauffeur au moment de la prise en charge.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReviewModal open={!!reviewBooking} onClose={() => setReviewBooking(null)} bookingId={reviewBooking?.id} targetUserId={reviewBooking?.driver_id} targetUserName={reviewBooking?.driver_name} onReviewed={fetchBookings} />

      <PassengerBottomNav />
    </div>
  );
}
